type ApiErrorLike = {
  response?: {
    data?: unknown;
  };
};

const firstString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = firstString(entry);

      if (message) {
        return message;
      }
    }
  }

  return undefined;
};

/**
 * Reads a single DRF field error, which may be either `string[]` or a bare
 * `string`. Indexing with `[0]` handles only the first shape — on a bare string
 * it yields the first character.
 */
export const getFieldErrorMessage = (value: unknown): string | undefined =>
  firstString(value);

const PREFERRED_KEYS = ['name', 'detail', 'msg', 'message'];

const fromRecord = (record: Record<string, unknown>): string | undefined => {
  const keys = [
    ...PREFERRED_KEYS,
    ...Object.keys(record).filter(key => !PREFERRED_KEYS.includes(key)),
  ];

  for (const key of keys) {
    const message = firstString(record[key]);

    if (message) {
      return message;
    }
  }

  return undefined;
};

/**
 * Pulls the first human-readable message out of a DRF error payload.
 *
 * Callers used to branch on `status === 400` and read `data.name[0]` directly,
 * which meant any other failure (a 5xx from an unhandled IntegrityError, a
 * network error with no `response` at all) was swallowed silently. This reads
 * whatever shape is available and always yields something to show.
 */
export const getApiErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as ApiErrorLike)?.response?.data;

  if (typeof data === 'string') {
    // 5xx responses are usually a full HTML error page — not worth showing.
    return (
      (data.trim().startsWith('<') ? undefined : firstString(data)) ?? fallback
    );
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return firstString(data) ?? fallback;
  }

  return fromRecord(data as Record<string, unknown>) ?? fallback;
};
